import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import QRCode from 'qrcode.react';
import { captureQRBatchCards, buildQRLabelsPDF } from '../../features/devices/utils/pdfGenerator';
import { DeviceList, DeviceForm, DeviceView, BarcodeScanner, QRBatchRenderer } from '../../features/devices/components';
import Pagination from '../../shared/components/common/Pagination';
import { ComplaintConfirmation, ComplaintForm } from '../../features/complaints/components';
import ConfirmationModal from '../../shared/components/modals/ConfirmationModal';
import ModalCloseButton from '../../shared/components/modals/ModalCloseButton';
import StatsCard from '../../shared/components/common/StatsCard';
import FilterHelpModal from '../../shared/components/modals/FilterHelpModal';
import MoreFiltersButton from '../../shared/components/common/MoreFiltersButton';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { createInitialFilterState } from '../../features/devices/hooks/useFilterState';
import { fetchFilteredDevices, fetchAllFilteredDeviceIds, formatPaginationForApi, isDeviceFilterKeyActive } from '../../features/devices/utils';
import DeviceActiveFilterPills from '../../features/devices/components/DeviceActiveFilterPills';
import { exportFilteredDevices } from '../../utils/exportUtils';
import { API_ENDPOINTS } from '../../config/apiConfig';
import LocationFilterMultiSelect from '../../shared/components/common/LocationFilterMultiSelect';
import DisabledLocationAccessBlockedBanner from '../../shared/components/common/DisabledLocationAccessBlockedBanner';
import {
  getDisabledLocationsFromSelection,
} from '../../shared/utils/locationDisplay';
import { AVAILABILITY_IMPACT_FILTER_OPTIONS } from '../../shared/constants/availabilityImpactFilter';
import { DEVICE_CRITICALITY_OPTIONS } from '../../features/devices/constants/filterConstants';
import {
  getListPageSize,
  getDevicesFilter,
  getDevicesSort,
  saveUiPreferencePatch,
} from '../../shared/utils/uiPreferences';
import {
  applyDevicesFilterPreference,
  sortingFromDevicesSort,
} from '../../shared/utils/listDefaults';
import './Devices.css';

/**
 * Devices Container Component
 *
 * Centralized container for all device-related functionality:
 * - Filter state management (5-section architecture)
 * - Device CRUD operations
 * - UI orchestration (list, form, view, scanner)
 * - API communication
 *
 * @param {Object} props - Component props
 * @param {string} props.token - JWT authentication token
 * @param {Object} props.user - Current user object
 * @param {number} props.locationId - Current location ID
 * @param {Function} props.onNavigateToComplaintsWithFilter - Dashboard callback
 * @param {Function} [props.onNavigateToComplaintDetail] - Open complaints view for a complaint id (e.g. after create)
 * @returns {React.ReactNode} Devices container component
 */
function Devices({
  token,
  user,
  locationId,
  onNavigateToComplaintsWithFilter,
  onNavigateToComplaintDetail,
  onNavigateToMaintenanceHistory,
  onNavigateToAlertsWithFilter,
  onReturnToDashboard,
  onReturnToComplaints, // Callback to navigate back to complaints when device view is closed from complaints
  onReturnToAssignedDevices, // Callback to navigate back to My Devices when device view is closed from assigned-devices
  onReturnToDashboardFromDeviceView, // Callback to navigate back to dashboard when device view is closed from dashboard
  onReturnToAnalytics, // Callback to navigate back to Analytics when device view is closed from analytics
  onNavigateToLocationTransfers,
  onNavigateToDepartmentTransfers,
  fetchDevicesCallback, // Optional callback to parent for device list updates
  isRefreshing, // Optional external refresh state
  // Device view state from parent (App.js)
  viewingDeviceId: parentViewingDeviceId,
  showDeviceView: parentShowDeviceView,
  showForm: parentShowForm,
  selectedDevice: parentSelectedDevice,
  deviceViewSource: parentDeviceViewSource,
  setViewingDeviceId: parentSetViewingDeviceId,
  setShowDeviceView: parentSetShowDeviceView,
  setShowForm: parentSetShowForm,
  setSelectedDevice: parentSetSelectedDevice,
  setDeviceViewSource: parentSetDeviceViewSource
}) {
  const { canAccessModule } = useRoleAccess(user);
  const scanProcessedRef = useRef(false);
  const lastScannedSerialRef = useRef(null);

  // OPTIMIZATION: Caching for QR token decryption results (Option 1)
  const qrTokenCacheRef = useRef({});

  // OPTIMIZATION: Debouncing for duplicate scans (Option 4 - frontend level)
  const lastHandledScanRef = useRef(null);
  const lastHandledScanTimeRef = useRef(null);

  // ═══════════════════════════════════════════════════════════════════
  // SECTION A: CORE DEVICE DATA
  // ═══════════════════════════════════════════════════════════════════
  const [devices, setDevices] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [devicesSummary, setDevicesSummary] = useState({
    total_with_complaints: 0,
    overdue_ppm_count: 0,
    overdue_calibration_count: 0,
    overdue_compliance_count: 0,
    coverage_expiring_count: 0,
    coverage_not_covered_count: 0
  });
  const [isRefreshingDevices, setIsRefreshingDevices] = useState(false);

  // ═══════════════════════════════════════════════════════════════════
  // SECTION B: FILTER STATE (5-Section Architecture from Phase 2)
  // ═══════════════════════════════════════════════════════════════════
  const [filters, setFilters] = useState(() =>
    applyDevicesFilterPreference(getDevicesFilter(), createInitialFilterState())
  );
  const [activeFilterOrder, setActiveFilterOrder] = useState([]); // append-only ordering of activated filter keys

  const blockedDisabledLocations = useMemo(
    () => (user?.role === 'superadmin' ? getDisabledLocationsFromSelection(locations, filters.locations) : []),
    [user?.role, locations, filters.locations]
  );
  const isDisabledLocationFilterBlocked = blockedDisabledLocations.length > 0;

  // ═══════════════════════════════════════════════════════════════════
  // SECTION B2: PAGINATION STATE (Phase 3)
  // ═══════════════════════════════════════════════════════════════════
  // Get persisted per_page from user preferences (DB / local cache)
  const getInitialPerPage = () => getListPageSize();

  const [pagination, setPagination] = useState({
    page: 1,
    per_page: getInitialPerPage(),
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false
  });

  // ═══════════════════════════════════════════════════════════════════
  // SECTION B3: SORTING STATE
  // ═══════════════════════════════════════════════════════════════════
  const [sorting, setSorting] = useState(() => sortingFromDevicesSort(getDevicesSort()));

  // ═══════════════════════════════════════════════════════════════════
  // SECTION C: UI STATE
  // ═══════════════════════════════════════════════════════════════════
  // Use parent state if provided (from App.js), otherwise use local state
  const [localShowForm, setLocalShowForm] = useState(false);
  const [localSelectedDevice, setLocalSelectedDevice] = useState(null);
  const [localShowDeviceView, setLocalShowDeviceView] = useState(false);
  const [localViewingDeviceId, setLocalViewingDeviceId] = useState(null);

  // Determine which state to use (parent overrides local)
  const showForm = parentShowForm !== undefined ? parentShowForm : localShowForm;
  const setShowForm = parentSetShowForm || setLocalShowForm;
  const selectedDevice = parentSelectedDevice !== undefined ? parentSelectedDevice : localSelectedDevice;
  const setSelectedDevice = parentSetSelectedDevice || setLocalSelectedDevice;
  const showDeviceView = parentShowDeviceView !== undefined ? parentShowDeviceView : localShowDeviceView;
  const setShowDeviceView = parentSetShowDeviceView || setLocalShowDeviceView;
  const viewingDeviceId = parentViewingDeviceId !== undefined ? parentViewingDeviceId : localViewingDeviceId;
  const setViewingDeviceId = parentSetViewingDeviceId || setLocalViewingDeviceId;
  const deviceViewSource = parentDeviceViewSource !== undefined ? parentDeviceViewSource : 'devices';
  const setDeviceViewSource = parentSetDeviceViewSource || (() => {});

  const [showScanner, setShowScanner] = useState(false);
  const [scannerKey, setScannerKey] = useState(0);
  const [showComplaintForm, setShowComplaintForm] = useState(false);
  const [complaintDevice, setComplaintDevice] = useState(null);
  const [complaintConfirmation, setComplaintConfirmation] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [isDeletingDevice, setIsDeletingDevice] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    location: true,
    department: true,
    complaintFilters: true,
    maintenanceStatus: true,
    coverageStatus: true,
    characteristics: true,
    dateRange: true
  });
  const [showAdvancedPanel, setShowAdvancedPanel] = useState(false);
  const [showFilterHelp, setShowFilterHelp] = useState(false);
  const [drawerFilters, setDrawerFilters] = useState(createInitialFilterState());
  const [isClosingForm] = useState(false);

  // ═══════════════════════════════════════════════════════════════════
  // SECTION D: ROLE-BASED ACCESS
  // ═══════════════════════════════════════════════════════════════════
  const canEdit = user && (user.role === 'admin' || user.role === 'manager');

  // ═══════════════════════════════════════════════════════════════════
  // SECTION E: DEVICE SELECTION (Multi-select for bulk operations)
  // ═══════════════════════════════════════════════════════════════════
  const [selectedDeviceIds, setSelectedDeviceIds] = useState(new Set());
  const [selectAllDevices, setSelectAllDevices] = useState(false); // True if "Select All" is active across all pages
  const [isSelectingAllDevices, setIsSelectingAllDevices] = useState(false);
  const [showBulkQRModal, setShowBulkQRModal] = useState(false);
  const [bulkQRTokens, setBulkQRTokens] = useState([]);
  const [isBulkQRGenerating, setIsBulkQRGenerating] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isCancellingPDF, setIsCancellingPDF] = useState(false);
  const [pdfProgress, setPdfProgress] = useState({ processed: 0, total: 0, currentPage: 0, totalPages: 0, stage: null });

  // QR Batch Renderer state - for hidden component rendering (chunked during PDF gen)
  const [showQRBatchRenderer, setShowQRBatchRenderer] = useState(false);
  const [qrCaptureTokens, setQrCaptureTokens] = useState([]);
  const qrBatchRenderingCompleteRef = useRef(false);
  const pdfCancelRef = useRef(false);

  // QR Error Correction Level - Configurable for different document styles
  // 'L' = Low (7%) - Cleanest, most white space
  // 'M' = Medium (15%) - Balanced, matches sample documents
  // 'Q' = Quartile (25%) - High redundancy
  // 'H' = High (30%) - Most robust, very dense
  const [qrErrorCorrectionLevel, setQrErrorCorrectionLevel] = useState('M'); // Default 'M' to match sample_bulk_qr_labels.pdf

  // Ref to the bulk QR preview grid (no longer used for capturing, just display)
  const bulkQRPreviewGridRef = useRef(null);

  // Track whether we've already applied filters from navigation to avoid duplicate fetches
  // Initialize to true if there are no navigation filters (so EFFECT 1 runs normally)
  // Initialize to false if there are navigation filters (so EFFECT 1 waits for EFFECT 0)
  const [filterOptionsApplied, setFilterOptionsApplied] = React.useState(
    !window.deviceFilterOptions || Object.keys(window.deviceFilterOptions).length === 0
  );

  // ═══════════════════════════════════════════════════════════════════
  // PREVENT BODY SCROLL WHEN ADVANCED FILTER MODAL IS OPEN
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (showAdvancedPanel) {
      // Disable scrolling on body when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scroll is re-enabled if component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showAdvancedPanel]);

  // ═══════════════════════════════════════════════════════════════════
  // PREVENT BODY SCROLL WHEN BULK QR PREVIEW MODAL IS OPEN
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (showBulkQRModal) {
      // Disable scrolling on body when bulk QR preview modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scroll is re-enabled if component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showBulkQRModal]);

  // ═══════════════════════════════════════════════════════════════════
  // PREVENT BODY SCROLL WHEN PDF GENERATION PROGRESS BAR IS OPEN
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (isGeneratingPDF) {
      // Disable scrolling on body when PDF generation is in progress
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling when PDF generation is complete
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scroll is re-enabled if component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isGeneratingPDF]);

  // ═══════════════════════════════════════════════════════════════════
  // EFFECT 0: Apply filter options passed from dashboard navigation
  // This effect runs BEFORE EFFECT 1 and ensures filters are set with fetching
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (window.deviceFilterOptions && Object.keys(window.deviceFilterOptions).length > 0) {
      const filterOptions = window.deviceFilterOptions;

      // Build the new filter state
      const newFilters = { ...filters };

      // Apply dashboard navigation keys without dropping fields the list already supports
      [
        'hasActiveComplaints',
        'hasNoActiveComplaints',
        'ppmStatus',
        'calibrationStatus',
        'complianceStatus',
        'coverageStatus',
        'serviceContractTypes',
        'deviceStatus',
        'locations',
        'warrantyDateFrom',
        'warrantyDateTo',
        'contractDateFrom',
        'contractDateTo',
      ].forEach((key) => {
        if (filterOptions[key] !== undefined) {
          newFilters[key] = filterOptions[key];
        }
      });

      if (Array.isArray(newFilters.locations) && newFilters.locations.length > 0) {
        newFilters.locations = newFilters.locations.map((id) => String(id));
        setDrawerFilters((prev) => ({ ...prev, locations: newFilters.locations }));
        setActiveFilterOrder((prev) => (prev.includes('locations') ? prev : [...prev, 'locations']));
      }

      const nextSorting = filterOptions.sort_by
        ? {
          sort_by: String(filterOptions.sort_by),
          sort_direction: filterOptions.sort_direction === 'asc' ? 'asc' : 'desc',
        }
        : sorting;

      // Clear the stored options after reading them
      delete window.deviceFilterOptions;

      // Update filters AND fetch with the new filter state immediately
      // This prevents race condition where EFFECT 1 runs with old state
      if (token && (locationId || user?.role === 'superadmin')) {
        setFilters(newFilters);
        if (filterOptions.sort_by) {
          setSorting(nextSorting);
        }

        // Call fetchFilteredDevices directly with the new filters to avoid state update race
        (async () => {
          try {
            const currentPerPage = pagination.per_page || 25;
            const formattedPagination = formatPaginationForApi(1, currentPerPage);
            const sortingParam = nextSorting.sort_by ? {
              sort_by: nextSorting.sort_by,
              sort_direction: nextSorting.sort_direction
            } : null;
            const result = await fetchFilteredDevices(newFilters, formattedPagination, token, sortingParam);

            setDevices(result.devices || []);
            setPagination({
              page: result.pagination.page,
              per_page: result.pagination.per_page,
              total_count: result.pagination.total_count,
              total_pages: result.pagination.total_pages,
              has_next: result.pagination.has_next,
              has_previous: result.pagination.has_previous
            });

            if (result.summary) {
              setDevicesSummary(result.summary);
            }
          } catch (error) {
            console.error('[Devices.js] EFFECT 0: Error fetching devices:', error);
          }
        })();
      }

      setFilterOptionsApplied(true);
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // EFFECT 1: Fetch devices when filters or sorting changes (reset to page 1)
  // SKIP on initial mount if filter options from navigation are being applied
  // Also reset device selection when filters change
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Skip on initial mount when EFFECT 0 is applying navigation filters
    // EFFECT 0 will handle the initial fetch, so EFFECT 1 should only run for
    // subsequent filter changes (e.g., user clicking preset buttons, advanced filters)
    if (!filterOptionsApplied) {
      return;
    }

    if (token && (locationId || user?.role === 'superadmin')) {
      // Reset device selection when filters change
      setSelectedDeviceIds(new Set());
      setSelectAllDevices(false);

      // Capture current per_page before state update
      const currentPerPage = pagination.per_page || 25;

      // Reset pagination to page 1 when filters or sorting changes
      setPagination(prev => ({ ...prev, page: 1 }));

      // Fetch with page 1 but preserve the user's per_page preference
      fetchDevices({ page: 1, per_page: currentPerPage });
      fetchDepartments();
      if (canAccessModule('locations')) {
        fetchLocations();
      }
    }
  }, [
    filterOptionsApplied, // Gate that prevents initial run when applying navigation filters
    token,
    locationId,
    filters.locations,
    filters.departments,
    filters.search,
    filters.installationDateFrom,
    filters.installationDateTo,
    filters.warrantyDateFrom,
    filters.warrantyDateTo,
    filters.contractDateFrom,
    filters.contractDateTo,
    // Filter fields from Phase 3 (Backend)
    filters.ppmStatus,
    filters.calibrationStatus,
    filters.complianceStatus,
    filters.coverageStatus,
    filters.serviceContractTypes,
    filters.criticality,
    filters.deviceStatus,
    filters.complianceRequired,
    filters.calibrationRequired,
    filters.productCostFrom,
    filters.productCostTo,
    filters.hasActiveComplaints,
    filters.hasNoActiveComplaints,
    filters.impactsAvailability,
    // Sorting fields
    sorting.sort_by,
    sorting.sort_direction
  ]);

  // ═══════════════════════════════════════════════════════════════════
  // EFFECT 2: Fetch devices when pagination changes (same filters)
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Skip if we're still applying navigation filters
    // EFFECT 0 handles the initial fetch when applying navigation filters
    if (!filterOptionsApplied) {
      return;
    }

    // Fetch when pagination changes (page or per_page)
    if (token && (locationId || user?.role === 'superadmin')) {
      fetchDevices(pagination);
    }
  }, [token, locationId, user, filterOptionsApplied, pagination.page, pagination.per_page]);

  // ═══════════════════════════════════════════════════════════════════
  // DATA FETCHING FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Fetch filtered and paginated devices from backend
   *
   * This function:
   * 1. Calls the hybrid backend filter endpoint (POST /api/devices/filter)
   * 2. Sends pagination parameters (page, per_page)
   * 3. Sends all filter state to backend for Tier 1/2/3 processing
   * 4. Sends sorting parameters for server-side sort
   * 5. Updates component state with filtered results and pagination metadata
   * 6. Notifies parent via callback with filtered results
   *
   * Filter Architecture (Phase 3 - Hybrid Backend/Frontend):
   * - Tier 1 (Backend): Pure SQL filters - department, service type, requirements, cost, complaints
   * - Tier 2 (Backend): Date-based calculations - PPM/calibration/compliance/warranty/contract status
   * - Tier 3 (Backend): Text search - device ID, name, serial, model, manufacturer
   * - Server Sort: Applied AFTER filtering, BEFORE pagination for accurate global sorting
   *
   * @param {Object} paginationOverride - Optional pagination override (page, per_page)
   */
  const fetchDevices = async (paginationOverride = null) => {
    try {
      setIsRefreshingDevices(true);

      if (isDisabledLocationFilterBlocked) {
        setDevices([]);
        setPagination((prev) => ({ ...prev, total_count: 0, total_pages: 0, has_next: false, has_previous: false }));
        if (fetchDevicesCallback) {
          fetchDevicesCallback([]);
        }
        return;
      }

      // Use provided pagination or current state pagination
      const paginationParams = paginationOverride || pagination;
      const formattedPagination = formatPaginationForApi(paginationParams.page, paginationParams.per_page);

      // Build sorting parameter (only if sort_by is set)
      const sortingParam = sorting.sort_by ? {
        sort_by: sorting.sort_by,
        sort_direction: sorting.sort_direction
      } : null;

      // Call backend filter endpoint with current filters, pagination, and sorting
      const result = await fetchFilteredDevices(filters, formattedPagination, token, sortingParam);

      // Update devices list
      setDevices(result.devices || []);

      // Update pagination metadata from response
      setPagination({
        page: result.pagination.page,
        per_page: result.pagination.per_page,
        total_count: result.pagination.total_count,
        total_pages: result.pagination.total_pages,
        has_next: result.pagination.has_next,
        has_previous: result.pagination.has_previous
      });

      // Update summary stats from backend (covers entire filtered set, not just current page)
      if (result.summary) {
        setDevicesSummary(result.summary);
      }

      // Notify parent if callback provided
      if (fetchDevicesCallback) {
        fetchDevicesCallback(result.devices || []);
      }
    } catch (error) {
      console.error('[Devices.js] Error fetching devices:', error);

      // Handle specific error cases
      if (error.code === 'UNAUTHORIZED') {
        toast.error('Session expired. Please login again.');
        // Could trigger logout here
      } else if (error.code === 'FORBIDDEN') {
        toast.error('You do not have permission to view devices.');
      } else if (error.code === 'NETWORK_ERROR') {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error(error.message || 'Failed to fetch devices');
      }

      // Reset devices and pagination on error
      setDevices([]);
      setPagination(prev => ({ ...prev, total_count: 0, total_pages: 0 }));
    } finally {
      setIsRefreshingDevices(false);
    }
  };

  /**
   * Fetch department list for filter dropdown
   */
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.DEPARTMENTS.LIST, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    }
  };

  /**
   * Fetch location list for filter dropdown
   */
  const fetchLocations = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.LOCATIONS.LIST, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setLocations(response.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // DEVICE CRUD OPERATIONS
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Add new device
   */
  const handleAddDevice = async (deviceData) => {
    try {
      const deviceDataWithLocation = {
        ...deviceData,
        location_id: locationId
      };

      const response = await axios.post(API_ENDPOINTS.DEVICES.CREATE, deviceDataWithLocation, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const newDevice = response.data;
      const newDeviceId = response.data.id;

      if (user?.role === 'manager') {
        setSelectedDevice(newDevice);
        toast.success('Device saved as draft! You can now upload documents and submit for approval.');
      } else {
        setShowForm(false);
        setSelectedDevice(null);
        setViewingDeviceId(newDeviceId);
        setShowDeviceView(true);

        if (newDevice.device_status === 'pending_approval') {
          toast.success('Device created successfully and submitted for admin approval');
        } else {
          toast.success('Device created successfully!');
        }
      }

      fetchDevices();
    } catch (error) {
      console.error('Error adding device:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error(error.response?.data?.error || 'Failed to add device');
      }
    }
  };

  /**
   * Update existing device
   */
  const handleUpdateDevice = async (deviceId, deviceData) => {
    try {
      await axios.put(API_ENDPOINTS.DEVICES.UPDATE(deviceId), deviceData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const isEditResubmitMode = selectedDevice?.device_status === 'in_draft' && user?.role === 'manager';

      if (isEditResubmitMode) {
        toast.success('Changes saved successfully!');
      } else {
        toast.success('Device updated successfully!');
        setShowForm(false);
        setSelectedDevice(null);
        setViewingDeviceId(deviceId);
        setShowDeviceView(true);
      }

      fetchDevices();
    } catch (error) {
      console.error('Error updating device:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to update device');
      }
    }
  };

  /**
   * Delete device (admin only)
   * Shows confirmation modal instead of window.confirm
   */
  const handleDeleteDevice = (deviceId, deviceName) => {
    setDeviceToDelete({ id: deviceId, name: deviceName });
    setShowDeleteConfirmation(true);
  };

  /**
   * Confirm device deletion after user confirms in modal
   */
  const handleConfirmDelete = async () => {
    if (!deviceToDelete) return;

    setIsDeletingDevice(true);

    try {
      await axios.delete(API_ENDPOINTS.DEVICES.DELETE(deviceToDelete.id), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      fetchDevices();
      toast.success('Device deleted successfully!');
      setShowDeleteConfirmation(false);
      setDeviceToDelete(null);
    } catch (error) {
      console.error('Error deleting device:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to delete device');
      }
    } finally {
      setIsDeletingDevice(false);
    }
  };

  /**
   * Cancel device deletion
   */
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setDeviceToDelete(null);
  };

  // ═══════════════════════════════════════════════════════════════════
  // UI EVENT HANDLERS
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Handle device view (read-only mode)
   */
  const handleViewDevice = (device) => {
    setViewingDeviceId(device.id);
    setShowDeviceView(true);
    setShowForm(false);
  };

  /**
   * Handle device edit
   */
  const handleEditDevice = (device) => {
    setSelectedDevice(device);
    setShowForm(true);
    setShowDeviceView(false);
  };

  /**
   * Handle successful device submission for approval
   * Called from DeviceForm when user submits a device for admin review
   *
   * Instead of closing the form, we refresh the device data from the server
   * so the form can display the pending_approval status with appropriate messages
   */
  const handleSubmissionSuccess = async (result) => {
    try {
      // Fetch the fresh device data to get updated status
      const response = await axios.get(API_ENDPOINTS.DEVICES.GET(result.id || selectedDevice?.id), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data) {
        // Update the selected device with fresh data showing pending_approval status
        // This keeps the form open but now displays the device in pending_approval mode
        setSelectedDevice(response.data);
      }
    } catch (error) {
      console.error('Error refreshing device after submission:', error);
      // Even if refresh fails, close the form and show device list
      setShowForm(false);
      setSelectedDevice(null);
    }

    // Refresh the device list in the background
    fetchDevices();
  };

  /**
   * Close device view
   */
  const handleCloseDeviceView = () => {
    setShowDeviceView(false);
    setViewingDeviceId(null);
  };

  /**
   * Handle sort column change from DeviceList
   * Updates sorting state which triggers EFFECT 1 to fetch with new sort
   */
  const handleSortChange = (column) => {
    setSorting(prev => {
      // If same column clicked, toggle direction; otherwise set new column
      if (prev.sort_by === column) {
        return {
          ...prev,
          sort_direction: prev.sort_direction === 'asc' ? 'desc' : 'asc'
        };
      }
      // Cost columns default to highest-first so dashboard handoff matches list UX
      const costColumns = ['product_cost', 'service_contract_cost'];
      return {
        sort_by: column,
        sort_direction: costColumns.includes(column) ? 'desc' : 'asc'
      };
    });
  };

  const [isExportingDevices, setIsExportingDevices] = useState(false);

  const handleExportDevicesExcel = async (selectedColumns) => {
    if (!token) return;
    setIsExportingDevices(true);
    try {
      const { exportedRowCount } = await exportFilteredDevices(filters, sorting, 'excel', token, selectedColumns);
      const count = exportedRowCount ?? pagination.total_count;
      toast.success(
        count > 0
          ? `Exported ${count} device${count !== 1 ? 's' : ''} to Excel`
          : 'Export complete'
      );
    } catch (error) {
      console.error('Error exporting devices Excel:', error);
      toast.error(error.message || 'Failed to export Excel');
    } finally {
      setIsExportingDevices(false);
    }
  };

  const handleExportDevicesPDF = async (selectedColumns) => {
    if (!token) return;
    setIsExportingDevices(true);
    try {
      const { exportedRowCount } = await exportFilteredDevices(filters, sorting, 'pdf', token, selectedColumns);
      const count = exportedRowCount ?? pagination.total_count;
      toast.success(
        count > 0
          ? `Exported ${count} device${count !== 1 ? 's' : ''} to PDF`
          : 'Export complete'
      );
    } catch (error) {
      console.error('Error exporting devices PDF:', error);
      toast.error(error.message || 'Failed to export PDF');
    } finally {
      setIsExportingDevices(false);
    }
  };

  /**
   * Close form and return to previous state
   */
  const handleCancelForm = () => {
    const isEditResubmitMode = selectedDevice?.device_status === 'in_draft' && user?.role === 'manager';

    if (isEditResubmitMode) {
      setShowForm(false);
      setShowDeviceView(false);
      setSelectedDevice(null);
      setViewingDeviceId(null);
    } else if (selectedDevice?.id) {
      setViewingDeviceId(selectedDevice.id);
      setShowDeviceView(true);
      setShowForm(false);
      setSelectedDevice(null);
    } else {
      setShowForm(false);
      setShowDeviceView(false);
      setSelectedDevice(null);
    }
  };

  /**
   * Handle complaint report
   */
  const handleReportComplaint = (device) => {
    if (device?.device_status === 'in_transfer') {
      toast.info('Complaints cannot be created while this device is in a transfer');
      return;
    }
    setComplaintDevice(device);
    setShowComplaintForm(true);
  };

  /**
   * Handle complaint success - refresh devices
   */
  const handleComplaintSuccess = (confirmationPayload) => {
    if (confirmationPayload) {
      setComplaintConfirmation(confirmationPayload);
    }
    fetchDevices();
  };

  /**
   * Handle device scan (barcode/QR code)
   */
  const handleScanDevice = async (scannedCode) => {
    setShowScanner(false);

    try {
      // OPTIMIZATION: Debounce duplicate scans at frontend level (Option 4)
      // Very lenient window (500ms) - only blocks accidental double-taps
      // Caching (Option 1) handles actual duplicate prevention
      const now = Date.now();
      if (lastHandledScanRef.current === scannedCode &&
          lastHandledScanTimeRef.current &&
          now - lastHandledScanTimeRef.current < 500) {
        console.log('Duplicate scan detected within 500ms, ignoring');
        return;
      }

      // Record this scan for debounce checking
      lastHandledScanRef.current = scannedCode;
      lastHandledScanTimeRef.current = now;

      let deviceIdentifier = null;

      // Check if scanned code is a full URL (for QR codes with base64 tokens)
      if (scannedCode.includes('/device-scan')) {
        try {
          const url = new URL(scannedCode);
          // Extract token from URL path - it's the last segment after /device-scan
          // Format: https://yourdomain.com/device-scan?token={token}
          let tokenParam = url.searchParams.get('token');

          if (tokenParam) {
            // OPTIMIZATION: Check cache before making API call (Option 1)
            if (qrTokenCacheRef.current[tokenParam]) {
              console.log('Using cached QR token decryption result');
              deviceIdentifier = qrTokenCacheRef.current[tokenParam];
            } else {
              // CRITICAL FIX: Backend handles base64 decoding, not frontend
              // Frontend should call the QR endpoint which decodes the token server-side
              // This prevents cryptographic manipulation and ensures proper decoding
              try {
                // SECURITY FIX: /api/devices/qr/{token} endpoint does NOT require authentication
                // because it validates organization/device ownership via base64-encoded token only
                // Token format: base64(org_id|device_identifier)
                const qrDeviceResponse = await axios.get(
                  API_ENDPOINTS.DEVICES.QR_DEVICE(tokenParam)
                  // No auth header needed - token provides all necessary context and validation
                );

                // Extract device identifier from the decoded response
                if (qrDeviceResponse.data && qrDeviceResponse.data.device_identifier) {
                  deviceIdentifier = qrDeviceResponse.data.device_identifier;

                  // OPTIMIZATION: Cache the decoding result for 1 hour (Option 1)
                  qrTokenCacheRef.current[tokenParam] = deviceIdentifier;
                  console.log('QR token cached for future scans');
                } else {
                  console.error('Invalid response from QR device endpoint:', qrDeviceResponse.data);
                  toast.error('Failed to parse the scanned QR code. Please verify the code is not damaged or expired.');
                  return;
                }
              } catch (decodeError) {
                console.error('Error decoding QR token via backend:', decodeError);

                // Check if it's an authentication or network error
                if (decodeError.response?.status === 401 || decodeError.response?.status === 403) {
                  toast.error('You do not have permission to access this QR code.');
                } else {
                  toast.error(decodeError.response?.data?.message || 'Failed to parse the scanned QR code. Please verify the code is not damaged or expired.');
                }
                return;
              }
            }
          }
        } catch (urlError) {
          console.error('Error parsing scanned URL:', urlError);
          toast.error('Failed to parse the scanned QR code. Please verify the code is not damaged or expired.');
          return;
        }
      }

      if (deviceIdentifier) {
        const encodedCode = encodeURIComponent(deviceIdentifier);
        const response = await axios.get(`${API_ENDPOINTS.DEVICES.LIST}/lookup/id/${encodedCode}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        const device = response.data;
        setViewingDeviceId(device.id);
        setShowDeviceView(true);
        setShowForm(false);
        setSelectedDevice(null);
      } else {
          toast.error("The QR code is invalid. Please check the code and try again!");
      }
    } catch (error) {
      console.error('Error looking up device:', error);
      if (error.response?.status === 404) {
        const errorMsg = error.response?.data?.error || `No device found with code: ${scannedCode}`;
        if (errorMsg.includes('not accessible in your department')) {
          toast.error('Device not found in your department');
        } else {
          toast.error(errorMsg);
        }
      } else {
        toast.error('Failed to lookup device');
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // DEVICE SELECTION HANDLERS
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Toggle selection of a single device
   */
  const handleToggleDeviceSelection = (deviceId) => {
    setSelectAllDevices(false);
    setSelectedDeviceIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deviceId)) {
        newSet.delete(deviceId);
      } else {
        newSet.add(deviceId);
      }
      return newSet;
    });
  };

  /**
   * Select or deselect all devices matching current filters (across all pages).
   * Paginates at the API max (100 per page) until every matching device is loaded.
   */
  const handleToggleSelectAll = async () => {
    if (selectAllDevices || isSelectingAllDevices) {
      setSelectedDeviceIds(new Set());
      setSelectAllDevices(false);
      return;
    }

    setIsSelectingAllDevices(true);
    try {
      const sortingParam = sorting.sort_by ? {
        sort_by: sorting.sort_by,
        sort_direction: sorting.sort_direction
      } : null;

      const allIds = await fetchAllFilteredDeviceIds(filters, token, sortingParam);

      if (allIds.length > 0) {
        setSelectedDeviceIds(new Set(allIds));
        setSelectAllDevices(true);
      } else {
        toast.error('No devices found matching current filters');
      }
    } catch (error) {
      console.error('Error fetching all devices for selection:', error);
      toast.error('Failed to select all devices. Please try again.');
    } finally {
      setIsSelectingAllDevices(false);
    }
  };

  /**
   * Generate QR tokens for all selected devices
   */
  const handleGenerateBulkQRCodes = async () => {
    if (selectedDeviceIds.size === 0) {
      return;
    }

    setIsBulkQRGenerating(true);

    try {
      const deviceIds = Array.from(selectedDeviceIds);

      const response = await axios.post(
        API_ENDPOINTS.DEVICES.BULK_QR_TOKENS,
        { device_ids: deviceIds },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setBulkQRTokens(response.data.tokens || []);
        setShowBulkQRModal(true);
      } else {
        console.error('Failed to generate QR codes:', response.data.message);
      }
    } catch (error) {
      console.error('Error generating bulk QR codes:', error);
    } finally {
      setIsBulkQRGenerating(false);
    }
  };

  /**
   * Close bulk QR modal. If generation is running, cancel it first.
   */
  const handleCloseBulkQRModal = () => {
    if (isGeneratingPDF) {
      pdfCancelRef.current = true;
      setIsCancellingPDF(true);
      setIsGeneratingPDF(false);
      setShowQRBatchRenderer(false);
      setQrCaptureTokens([]);
      setPdfProgress({ processed: 0, total: 0, currentPage: 0, totalPages: 0, stage: null });
      setIsCancellingPDF(false);
    } else {
      setSelectedDeviceIds(new Set());
      setSelectAllDevices(false);
    }

    setShowBulkQRModal(false);
    setBulkQRTokens([]);
  };

  const handleCancelPDFGeneration = () => {
    pdfCancelRef.current = true;
    setIsCancellingPDF(true);
  };

  const waitForQRBatchRender = () =>
    new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (pdfCancelRef.current) {
          clearInterval(checkInterval);
          clearTimeout(safetyTimeout);
          const err = new Error('QR label generation cancelled');
          err.name = 'AbortError';
          reject(err);
          return;
        }
        if (qrBatchRenderingCompleteRef.current) {
          clearInterval(checkInterval);
          clearTimeout(safetyTimeout);
          resolve();
        }
      }, 50);

      const safetyTimeout = setTimeout(() => {
        clearInterval(checkInterval);
        resolve();
      }, 8000);
    });

  const getBulkQRLocationName = () =>
    (devices && devices.length > 0 && devices[0].location_name)
      ? devices[0].location_name
      : (user?.location_name || 'LOC');

  const buildBulkQRFilename = () => {
    const orgPrefix = (user?.organization_name || 'ORG').replace(/\s+/g, '_').toUpperCase().substring(0, 20);
    const locPrefix = getBulkQRLocationName().replace(/\s+/g, '_').toUpperCase().substring(0, 20);
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
    return `${orgPrefix}_${locPrefix}_BulkQRLabels_${dateStr}_${timeStr}.pdf`;
  };

  /**
   * Render/capture QR labels in chunks of 20 so large batches stay fast
   * (full DOM with hundreds of SVGs makes each html2canvas call much slower).
   */
  const generateBulkQRPdfBlob = async () => {
    const total = bulkQRTokens.length;
    const chunkSize = 20;
    const allImages = [];
    let lastUiUpdateAt = 0;

    const throwIfCancelled = () => {
      if (pdfCancelRef.current) {
        const err = new Error('QR label generation cancelled');
        err.name = 'AbortError';
        throw err;
      }
    };

    const reportProgress = (processed, stage = 'capturing', extra = {}) => {
      const now = Date.now();
      // Throttle UI updates so Cancel doesn't feel like progress is "stuttering"
      if (
        processed >= total ||
        processed <= 1 ||
        stage !== 'capturing' ||
        now - lastUiUpdateAt >= 120
      ) {
        lastUiUpdateAt = now;
        if (!pdfCancelRef.current) {
          setPdfProgress((prev) => ({
            ...prev,
            processed,
            total,
            stage,
            ...extra
          }));
        }
      }
    };

    setShowQRBatchRenderer(true);
    setPdfProgress((prev) => ({ ...prev, stage: 'capturing', processed: 0, total }));

    for (let offset = 0; offset < total; offset += chunkSize) {
      throwIfCancelled();
      const chunk = bulkQRTokens.slice(offset, offset + chunkSize);

      qrBatchRenderingCompleteRef.current = false;
      setQrCaptureTokens(chunk);
      // Allow React to commit the chunk before waiting for render-complete
      await new Promise((resolve) => setTimeout(resolve, 0));
      await waitForQRBatchRender();
      throwIfCancelled();

      const chunkImages = await captureQRBatchCards({
        expectedCount: chunk.length,
        targetDPI: 200,
        isCancelled: () => pdfCancelRef.current,
        onCardCaptured: (localIdx) => {
          reportProgress(offset + localIdx + 1, 'capturing');
        }
      });

      allImages.push(...chunkImages);
      reportProgress(Math.min(offset + chunk.length, total), 'capturing');
    }

    throwIfCancelled();
    setShowQRBatchRenderer(false);
    setQrCaptureTokens([]);
    setPdfProgress((prev) => ({ ...prev, stage: 'layout' }));

    return buildQRLabelsPDF(allImages, bulkQRTokens, {
      orgName: user?.organization_name || 'ORG',
      locationName: getBulkQRLocationName(),
      isCancelled: () => pdfCancelRef.current,
      onProgress: (progress) => {
        if (!pdfCancelRef.current) {
          setPdfProgress((prev) => ({ ...prev, ...progress }));
        }
      }
    });
  };

  /**
   * Download PDF directly without opening print preview
   */
  const handleDownloadBulkPDF = async () => {
    pdfCancelRef.current = false;
    setIsCancellingPDF(false);
    setIsGeneratingPDF(true);
    setSelectedDeviceIds(new Set());
    setSelectAllDevices(false);
    setPdfProgress({ processed: 0, total: bulkQRTokens.length, currentPage: 0, totalPages: 0, stage: 'initializing' });

    try {
      const pdfBlob = await generateBulkQRPdfBlob();

      if (pdfCancelRef.current) {
        const err = new Error('QR label generation cancelled');
        err.name = 'AbortError';
        throw err;
      }

      if (!pdfBlob || pdfBlob.size === 0) {
        throw new Error('Generated PDF is empty');
      }

      setPdfProgress(prev => ({ ...prev, stage: 'ready' }));
      const filename = buildBulkQRFilename();

      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(pdfBlob);
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadLink.href);

      setTimeout(() => {
        setIsGeneratingPDF(false);
        setIsCancellingPDF(false);
        setShowBulkQRModal(false);
        setBulkQRTokens([]);
        setShowQRBatchRenderer(false);
        setQrCaptureTokens([]);
        setPdfProgress({ processed: 0, total: 0, currentPage: 0, totalPages: 0, stage: null });
      }, 500);
    } catch (error) {
      const cancelled = error?.name === 'AbortError' || pdfCancelRef.current;
      if (cancelled) {
        toast.info('QR label generation cancelled');
      } else {
        console.error('❌ Error downloading PDF:', error);
        toast.error(`Failed to download QR labels: ${error.message}`);
      }
      setPdfProgress({ processed: 0, total: 0, currentPage: 0, totalPages: 0, stage: null });
      setShowQRBatchRenderer(false);
      setQrCaptureTokens([]);
      setIsGeneratingPDF(false);
      setIsCancellingPDF(false);
    }
  };


  /**
   * Generate PDF using hidden QRBatchRenderer (chunked) and open print dialog
   */
  const handleConfirmAndGeneratePDF = async () => {
    pdfCancelRef.current = false;
    setIsCancellingPDF(false);
    setIsGeneratingPDF(true);
    setSelectedDeviceIds(new Set());
    setSelectAllDevices(false);
    setPdfProgress({ processed: 0, total: bulkQRTokens.length, currentPage: 0, totalPages: 0, stage: 'initializing' });

    try {
      const pdfBlob = await generateBulkQRPdfBlob();

      if (pdfCancelRef.current) {
        const err = new Error('QR label generation cancelled');
        err.name = 'AbortError';
        throw err;
      }

      if (!pdfBlob || pdfBlob.size === 0) {
        throw new Error('Generated PDF is empty');
      }

      setPdfProgress(prev => ({ ...prev, stage: 'ready' }));
      const filename = buildBulkQRFilename();
      const previewUrl = URL.createObjectURL(pdfBlob);

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = previewUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        try {
          iframe.contentWindow.document.title = filename;
          iframe.contentWindow.print();

          const closeDelay = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
            ? 2500
            : 800;

          setTimeout(() => {
            setIsGeneratingPDF(false);
            setIsCancellingPDF(false);
            setShowBulkQRModal(false);
          }, closeDelay);
        } catch (error) {
          console.error('Error triggering print:', error);
        }
      };

      const iframeTimeout = setTimeout(() => {
        if (!iframe.contentWindow) {
          setIsGeneratingPDF(false);
          setIsCancellingPDF(false);
          setShowBulkQRModal(false);
        }
      }, 5000);

      const cleanupDelay = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        ? 8000
        : 4000;

      setTimeout(() => {
        try {
          clearTimeout(iframeTimeout);
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
          URL.revokeObjectURL(previewUrl);
        } catch (error) {
          console.error('Error cleaning up iframe:', error);
        }
      }, cleanupDelay);

      setTimeout(() => {
        setBulkQRTokens([]);
        setShowQRBatchRenderer(false);
        setQrCaptureTokens([]);
        setPdfProgress({ processed: 0, total: 0, currentPage: 0, totalPages: 0, stage: null });
      }, 5000);
    } catch (error) {
      const cancelled = error?.name === 'AbortError' || pdfCancelRef.current;
      if (cancelled) {
        toast.info('QR label generation cancelled');
      } else {
        console.error('❌ Error generating PDF:', error);
        toast.error(`Failed to print QR labels: ${error.message}`);
      }
      setPdfProgress({ processed: 0, total: 0, currentPage: 0, totalPages: 0, stage: null });
      setShowQRBatchRenderer(false);
      setQrCaptureTokens([]);
      setIsGeneratingPDF(false);
      setIsCancellingPDF(false);
    }
  };


  // ═══════════════════════════════════════════════════════════════════
  // FILTER HELPERS
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Helper to render filter section header with tooltip
   * Shows "ⓘ" icon that explains OR logic within sections
   */
  const FilterSectionHeader = ({ icon, title, tooltip }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'flex-start' }}>
      <span>{icon}</span>
      <span>{title}</span>
      {tooltip && (
        <span
          className="filter-section-tooltip"
          title={tooltip}
          style={{
            cursor: 'help',
            opacity: 0.6,
            fontSize: '0.85rem',
            fontWeight: 'bold'
          }}
        >
          ⓘ
        </span>
      )}
    </div>
  );

  const hasActiveDateFilters = () => {
    return !!(filters.installationDateFrom || filters.installationDateTo ||
              filters.warrantyDateFrom || filters.warrantyDateTo ||
              filters.contractDateFrom || filters.contractDateTo);
  };

  const hasActiveDepartmentFilters = () => {
    return filters.departments.length > 0;
  };

  const hasActiveLocationFilters = () => {
    return filters.locations.length > 0;
  };

  const hasActiveAdvancedFilters = () => {
    return !!(
      // Section 1: Maintenance Status
      (filters.ppmStatus && filters.ppmStatus.length > 0) ||
      (filters.calibrationStatus && filters.calibrationStatus.length > 0) ||
      (filters.complianceStatus && filters.complianceStatus.length > 0) ||
      // Section 2: Coverage Status
      (filters.coverageStatus && filters.coverageStatus.length > 0) ||
      // Section 3: Characteristics
      (filters.serviceContractTypes && filters.serviceContractTypes.length > 0) ||
      (filters.criticality && filters.criticality.length > 0) ||
      (filters.deviceStatus && filters.deviceStatus.length > 0) ||
      filters.complianceRequired ||
      filters.calibrationRequired ||
      filters.productCostFrom ||
      filters.productCostTo
    );
  };

  const hasAdditionalFilters = Boolean(
    hasActiveDateFilters()
    || hasActiveDepartmentFilters()
    || hasActiveLocationFilters()
    || (filters.serviceContractTypes && filters.serviceContractTypes.length > 0)
    || filters.complianceRequired
    || filters.calibrationRequired
    || filters.productCostFrom
    || filters.productCostTo
    || (filters.ppmStatus || []).some((status) => status !== 'Overdue (0-30d)' && status !== 'Overdue (30+d)')
    || (filters.calibrationStatus || []).some((status) => status !== 'Overdue (0-30d)' && status !== 'Overdue (30+d)')
    || (filters.complianceStatus || []).some((status) => status !== 'Overdue (0-30d)' && status !== 'Overdue (30+d)')
    || (filters.coverageStatus || []).some((status) => (
      status !== 'Active - Expiring Soon (≤30d)'
      && status !== 'Expired (0-30d)'
      && status !== 'Expired (30+d)'
    ))
    || (filters.deviceStatus || []).some((status) => status !== 'in_transfer')
    || (filters.criticality || []).some((value) => value !== 'critical' && value !== 'major' && value !== 'minor')
    || (filters.impactsAvailability && filters.impactsAvailability !== 'yes')
  );

  const hasAnyFiltersApplied = () => {
    // Free-text search is not an Active Filter (cleared via the search box ✕ only)
    return hasActiveDateFilters() ||
           hasActiveDepartmentFilters() ||
           hasActiveLocationFilters() ||
           hasActiveAdvancedFilters() ||
           filters.hasActiveComplaints ||
           filters.hasNoActiveComplaints ||
           (filters.impactsAvailability && filters.impactsAvailability !== '');
  };

  const isFilterKeyActive = isDeviceFilterKeyActive;

  // Maintain append-only order of filters as the user activates them
  useEffect(() => {
    const allKeys = [
      'departments',
      'locations',
      'ppmStatus',
      'calibrationStatus',
      'complianceStatus',
      'coverageStatus',
      'serviceContractTypes',
      'criticality',
      'deviceStatus',
      'complianceRequired',
      'calibrationRequired',
      'productCostRange',
      'installationDateRange',
      'warrantyDateRange',
      'contractDateRange',
      'hasActiveComplaints',
      'hasNoActiveComplaints',
      'impactsAvailability',
    ];

    setActiveFilterOrder(prevOrder => {
      let nextOrder = prevOrder.filter(key => allKeys.includes(key));

      // Remove keys that are no longer active
      nextOrder = nextOrder.filter(key => isFilterKeyActive(filters, key));

      // Append keys that became active but aren't yet in the order
      for (const key of allKeys) {
        if (isFilterKeyActive(filters, key) && !nextOrder.includes(key)) {
          nextOrder = [...nextOrder, key];
        }
      }

      return nextOrder;
    });
  }, [filters]);

  /**
   * Determine which quick filter preset is currently active
   * Returns the preset name or null if no preset matches
   *
   * Since presets now ADD to existing filters, we check if the specific
   * condition is met, regardless of other filters being present
   */
  const getCurrentActivePreset = () => {
    // Check if no filters applied (All Devices)
    const noOtherFilters = !hasActiveDateFilters() &&
                           !hasActiveDepartmentFilters() &&
                           !hasActiveAdvancedFilters() &&
                           !filters.hasActiveComplaints &&
                           !filters.hasNoActiveComplaints &&
                           !(filters.impactsAvailability && filters.impactsAvailability !== '');
    if (noOtherFilters) return 'all';

    // Return null - each preset button checks its own condition independently
    // This allows multiple presets to show as selected simultaneously
    // (though they're shown separately in the UI)
    return null;
  };

  /**
   * Helper functions for checking individual preset conditions
   *
   * These functions allow each quick filter button to independently check if its
   * specific filter condition is met, regardless of how the filters were set
   * (via preset button or advanced filter panel).
   *
   * Since these functions check the main `filters` state, they work correctly
   * whether filters are:
   * 1. Applied via preset buttons → applyPreset() → setFilters() (sets both values)
   * 2. Applied via advanced panel → manual selection (may set just one value)
   *
   * Logic: Button highlights if ANY of the preset values are selected, allowing
   * users to see visual feedback whether they select one or both status values.
   * For example:
   * - Selecting only "Overdue (0-30d)" from advanced filters highlights the button
   * - Selecting both "Overdue (0-30d)" and "Overdue (30+d)" also highlights the button
   * - Selecting neither does not highlight the button
   */
  const isOverduePPMActive = () => {
    return filters.ppmStatus?.some(status =>
      status === 'Overdue (0-30d)' || status === 'Overdue (30+d)'
    );
  };

  const isNeedsComplianceActive = () => {
    return filters.complianceStatus?.some(status =>
      status === 'Overdue (0-30d)' || status === 'Overdue (30+d)'
    );
  };

  const isNeedsCalibrationActive = () => {
    return filters.calibrationStatus?.some(status =>
      status === 'Overdue (0-30d)' || status === 'Overdue (30+d)'
    );
  };

  const isCoverageExpiringActive = () => {
    return filters.coverageStatus?.some(status =>
      status === 'Active - Expiring Soon (≤30d)'
    );
  };

  const isCoverageExpiredActive = () => {
    return filters.coverageStatus?.some(status =>
      status === 'Expired (0-30d)' || status === 'Expired (30+d)'
    );
  };

  const isInTransferActive = () => {
    return filters.deviceStatus?.includes('in_transfer');
  };

  const isCriticalityActive = (value) => filters.criticality?.includes(value);

  const toggleCriticalityFilter = (value) => {
    setFilters((prev) => {
      const current = prev.criticality || [];
      const isActive = current.includes(value);
      return {
        ...prev,
        criticality: isActive
          ? current.filter((item) => item !== value)
          : [...current, value],
      };
    });
  };

  // ═══════════════════════════════════════════════════════════════════
  // PRESET FILTER FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════

  const applyPreset = (presetName) => {
    switch (presetName) {
      case 'all':
        // Clear all filters
        setFilters(createInitialFilterState());
        break;
      case 'active-complaints':
        // Toggle active complaints filter
        setFilters(prev => ({ ...prev, hasActiveComplaints: !prev.hasActiveComplaints, hasNoActiveComplaints: false, impactsAvailability: '' }));
        break;
      case 'no-complaints':
        // Toggle no active complaints filter
        setFilters(prev => ({ ...prev, hasNoActiveComplaints: !prev.hasNoActiveComplaints, hasActiveComplaints: false, impactsAvailability: '' }));
        break;
      case 'availability-impact':
        setFilters(prev => ({
          ...prev,
          impactsAvailability: prev.impactsAvailability === 'yes' ? '' : 'yes',
          hasActiveComplaints: false,
          hasNoActiveComplaints: false,
        }));
        break;
      case 'in-transfer':
        setFilters(prev => {
          const isActive = prev.deviceStatus?.includes('in_transfer');
          if (isActive) {
            return {
              ...prev,
              deviceStatus: (prev.deviceStatus || []).filter((status) => status !== 'in_transfer'),
            };
          }
          const updated = [...(prev.deviceStatus || [])];
          if (!updated.includes('in_transfer')) updated.push('in_transfer');
          return { ...prev, deviceStatus: updated };
        });
        break;
      case 'overdue-ppm':
        // Toggle overdue PPM items - add both if neither is present, remove both if any are present
        setFilters(prev => {
          const hasAnyOverdue = prev.ppmStatus?.includes('Overdue (0-30d)') || prev.ppmStatus?.includes('Overdue (30+d)');
          if (hasAnyOverdue) {
            // Remove both overdue statuses
            const filtered = prev.ppmStatus.filter(status =>
              status !== 'Overdue (0-30d)' && status !== 'Overdue (30+d)'
            );
            return { ...prev, ppmStatus: filtered };
          } else {
            // Add both overdue statuses
            const updated = [...(prev.ppmStatus || [])];
            if (!updated.includes('Overdue (0-30d)')) updated.push('Overdue (0-30d)');
            if (!updated.includes('Overdue (30+d)')) updated.push('Overdue (30+d)');
            return { ...prev, ppmStatus: updated };
          }
        });
        break;
      case 'needs-compliance':
        // Toggle overdue compliance items - add both if neither is present, remove both if any are present
        setFilters(prev => {
          const hasAnyOverdue = prev.complianceStatus?.includes('Overdue (0-30d)') || prev.complianceStatus?.includes('Overdue (30+d)');
          if (hasAnyOverdue) {
            // Remove both overdue statuses
            const filtered = prev.complianceStatus.filter(status =>
              status !== 'Overdue (0-30d)' && status !== 'Overdue (30+d)'
            );
            return { ...prev, complianceStatus: filtered };
          } else {
            // Add both overdue statuses
            const updated = [...(prev.complianceStatus || [])];
            if (!updated.includes('Overdue (0-30d)')) updated.push('Overdue (0-30d)');
            if (!updated.includes('Overdue (30+d)')) updated.push('Overdue (30+d)');
            return { ...prev, complianceStatus: updated };
          }
        });
        break;
      case 'needs-calibration':
        // Toggle overdue calibration items - add both if neither is present, remove both if any are present
        setFilters(prev => {
          const hasAnyOverdue = prev.calibrationStatus?.includes('Overdue (0-30d)') || prev.calibrationStatus?.includes('Overdue (30+d)');
          if (hasAnyOverdue) {
            // Remove both overdue statuses
            const filtered = prev.calibrationStatus.filter(status =>
              status !== 'Overdue (0-30d)' && status !== 'Overdue (30+d)'
            );
            return { ...prev, calibrationStatus: filtered };
          } else {
            // Add both overdue statuses
            const updated = [...(prev.calibrationStatus || [])];
            if (!updated.includes('Overdue (0-30d)')) updated.push('Overdue (0-30d)');
            if (!updated.includes('Overdue (30+d)')) updated.push('Overdue (30+d)');
            return { ...prev, calibrationStatus: updated };
          }
        });
        break;
      case 'coverage-expiring':
        // Toggle coverage expiring status - toggle 'Active - Expiring Soon (≤30d)' on/off independently
        setFilters(prev => {
          const isActive = prev.coverageStatus?.includes('Active - Expiring Soon (≤30d)');
          if (isActive) {
            // Remove it
            const updated = prev.coverageStatus.filter(status => status !== 'Active - Expiring Soon (≤30d)');
            return { ...prev, coverageStatus: updated };
          } else {
            // Add it
            const updated = [...(prev.coverageStatus || [])];
            if (!updated.includes('Active - Expiring Soon (≤30d)')) updated.push('Active - Expiring Soon (≤30d)');
            return { ...prev, coverageStatus: updated };
          }
        });
        break;
      case 'coverage-expired':
        // Toggle coverage expired items - add both if neither is present, remove both if any are present
        setFilters(prev => {
          const hasAnyExpired = prev.coverageStatus?.includes('Expired (0-30d)') || prev.coverageStatus?.includes('Expired (30+d)');
          if (hasAnyExpired) {
            // Remove both expired statuses
            const filtered = prev.coverageStatus.filter(status =>
              status !== 'Expired (0-30d)' && status !== 'Expired (30+d)'
            );
            return { ...prev, coverageStatus: filtered };
          } else {
            // Add both expired statuses
            const updated = [...(prev.coverageStatus || [])];
            if (!updated.includes('Expired (0-30d)')) updated.push('Expired (0-30d)');
            if (!updated.includes('Expired (30+d)')) updated.push('Expired (30+d)');
            return { ...prev, coverageStatus: updated };
          }
        });
        break;
      default:
        break;
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════

  const refreshStatus = isRefreshing || isRefreshingDevices;

  return (
    <div className="devices-view">
      {!showForm && !showDeviceView && (
        <>
          <div className="devices-description-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <h1>🖥️ Device Management</h1>
              <button
                onClick={fetchDevices}
                className="btn-refresh"
                title="Refresh devices"
                style={{
                  padding: '0.5rem',
                  fontSize: '1.2rem',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  lineHeight: 1
                }}
              >
                🔄
              </button>
            </div>
            <p>View, search, filter, and manage all medical devices across departments. Create new devices, track maintenance schedules, and monitor device status.</p>
          </div>

          <div className="devices-header">
            <div className="header-left">
              <div className="stats-summary">
                <StatsCard
                  icon="📦"
                  number={pagination.total_count}
                  label={`Total${hasAnyFiltersApplied() ? ' (Filtered)' : ''}`}
                  className="total"
                />
                <StatsCard
                  icon="⚠️"
                  number={devicesSummary.total_with_complaints}
                  label="Complaints"
                  className="active"
                />
                <StatsCard
                  icon="🔧"
                  number={devicesSummary.overdue_ppm_count}
                  label="Overdue PPM"
                  className="maintenance"
                />
                <StatsCard
                  icon="⚖️"
                  number={devicesSummary.overdue_calibration_count}
                  label="Overdue Cal."
                  className="calibration"
                />
                <StatsCard
                  icon="⏰"
                  number={devicesSummary.coverage_expiring_count}
                  label="Coverage Expiring"
                  className="expiring"
                />
                <StatsCard
                  icon="🔓"
                  number={devicesSummary.coverage_not_covered_count}
                  label="No Coverage (In-house)"
                  className="uncovered"
                />
              </div>
            </div>
            {!showForm && !showDeviceView && (
              <div className="header-right">
                <div className="action-palette">
                  {canEdit && (
                    <button className="action-btn action-primary" onClick={() => setShowForm(true)}>
                      <span className="action-icon">➕</span>
                      <span className="action-text">Add Device</span>
                    </button>
                  )}
                  <button
                    className="action-btn action-secondary"
                    onClick={() => {
                      scanProcessedRef.current = false;
                      lastScannedSerialRef.current = null;
                      setShowForm(false);
                      setSelectedDevice(null);
                      setScannerKey(prev => prev + 1);
                      setShowScanner(true);
                    }}
                    title="Scan Barcode/QR Code"
                  >
                    <span className="action-icon">📷</span>
                    <span className="action-text">Scan Device</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {showDeviceView ? (
        <DeviceView
          deviceId={viewingDeviceId}
          token={token}
          onEdit={handleEditDevice}
          onClose={handleCloseDeviceView}
          user={user}
          isInEditResubmitMode={selectedDevice?.device_status === 'in_draft' && user?.role === 'manager'}
          onNavigateBack={() => {
            // Context-aware back navigation: return to the page the user came from
            const source = deviceViewSource;
            setShowDeviceView(false);
            setViewingDeviceId(null);
            setDeviceViewSource('devices'); // Reset source for next navigation

            if (source === 'complaints' && onReturnToComplaints) {
              onReturnToComplaints();
            } else if (source === 'assigned-devices' && onReturnToAssignedDevices) {
              onReturnToAssignedDevices();
            } else if (source === 'dashboard' && onReturnToDashboardFromDeviceView) {
              onReturnToDashboardFromDeviceView();
            } else if (source === 'analytics' && onReturnToAnalytics) {
              onReturnToAnalytics();
            }
            // Default: stay on devices list
          }}
          onNavigateToDashboard={onReturnToDashboard}
          onNavigateToLocationTransfers={
            user && ['admin', 'manager', 'technician'].includes(user.role)
              ? onNavigateToLocationTransfers
              : null
          }
          onNavigateToDepartmentTransfers={
            user?.role === 'custodian' ? onNavigateToDepartmentTransfers : null
          }
          onNavigateToComplaintsWithFilter={onNavigateToComplaintsWithFilter}
          onViewMaintenanceHistory={(filterOptions) => {
            // Navigate to maintenance history with device_identifier in search
            if (onNavigateToMaintenanceHistory) {
              onNavigateToMaintenanceHistory(filterOptions);
            }
          }}
        />
      ) : showForm && !isClosingForm && !showScanner && selectedDevice !== undefined ? (
        <DeviceForm
          key={selectedDevice?.id || 'new'}
          device={selectedDevice}
          onSubmit={selectedDevice ? handleUpdateDevice : handleAddDevice}
          onCancel={handleCancelForm}
          onSubmissionSuccess={handleSubmissionSuccess}
          onReturnToDashboard={onReturnToDashboard}
          token={token}
          user={user}
          onNavigateToComplaintsWithFilter={onNavigateToComplaintsWithFilter}
        />
      ) : (
        <>
          {/* Calculate recommended filename for display in preview modal */}
          {(() => {
            const orgName = user?.organization_name || 'ORG';
            let locationNode = 'LOC';
            if (devices && devices.length > 0 && devices[0].location_name) {
              locationNode = devices[0].location_name;
            } else if (user?.location_name) {
              locationNode = user.location_name;
            }
            window.recommendedFilenameParts = {
              orgPrefix: orgName.replace(/\s+/g, '_').toUpperCase().substring(0, 20),
              locPrefix: locationNode.replace(/\s+/g, '_').toUpperCase().substring(0, 20)
            };
            return null;
          })()}

          {/* Search Bar */}
          <div className="search-section">
            <div className="search-box-wrapper">
              <span className="search-icon-lens">🔍</span>
              <input
                type="text"
                placeholder="Search by Device ID, name, serial number, model, or manufacturer..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="search-input-clean"
              />
              {filters.search && (
                <button
                  className="clear-search-btn-inline"
                  onClick={() => setFilters({ ...filters, search: '' })}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* QUICK FILTERS (PRESETS) */}
          <div className="quick-filters">
            <div className="quick-filters-title">
              <svg className="filters-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Quick Filters
            </div>
            <div className="preset-buttons">
              <button
                className={`preset-btn ${getCurrentActivePreset() === 'all' ? 'active' : ''}`}
                onClick={() => applyPreset('all')}
              >
                All
              </button>
              <button
                className={`preset-btn ${filters.hasActiveComplaints ? 'active' : ''}`}
                onClick={() => applyPreset('active-complaints')}
              >
                Active Complaints
              </button>
              <button
                className={`preset-btn ${filters.hasNoActiveComplaints ? 'active' : ''}`}
                onClick={() => applyPreset('no-complaints')}
              >
                No Complaints
              </button>
              <button
                className={`preset-btn ${filters.impactsAvailability === 'yes' ? 'active' : ''}`}
                onClick={() => applyPreset('availability-impact')}
                title="Devices with active complaints impacting clinical availability — matches purple square marker on device name"
              >
                <span className="device-signal device-signal-impact" aria-hidden="true" />
                Availability Impact
              </button>
              <button
                className={`preset-btn ${isInTransferActive() ? 'active' : ''}`}
                onClick={() => applyPreset('in-transfer')}
                title="Devices currently in a location transfer (excluding drafts)"
              >
                In Transfer
              </button>
              <button
                className={`preset-btn ${isOverduePPMActive() ? 'active' : ''}`}
                onClick={() => applyPreset('overdue-ppm')}
              >
                Overdue PPM
              </button>
              <button
                className={`preset-btn ${isNeedsComplianceActive() ? 'active' : ''}`}
                onClick={() => applyPreset('needs-compliance')}
              >
                Needs Compliance
              </button>
              <button
                className={`preset-btn ${isNeedsCalibrationActive() ? 'active' : ''}`}
                onClick={() => applyPreset('needs-calibration')}
              >
                Needs Calibration
              </button>
              <button
                className={`preset-btn ${isCoverageExpiringActive() ? 'active' : ''}`}
                onClick={() => applyPreset('coverage-expiring')}
              >
                Coverage Expiring
              </button>
              <button
                className={`preset-btn ${isCoverageExpiredActive() ? 'active' : ''}`}
                onClick={() => applyPreset('coverage-expired')}
              >
                Coverage Expired
              </button>
              <button
                className={`preset-btn ${isCriticalityActive('critical') ? 'active' : ''}`}
                onClick={() => toggleCriticalityFilter('critical')}
                title="Critical devices — matches red circle marker on device name"
              >
                <span
                  className="device-signal device-signal-criticality device-signal-criticality--critical"
                  aria-hidden="true"
                />
                Critical
              </button>
              <button
                className={`preset-btn ${isCriticalityActive('major') ? 'active' : ''}`}
                onClick={() => toggleCriticalityFilter('major')}
                title="Major devices — matches amber circle marker on device name"
              >
                <span
                  className="device-signal device-signal-criticality device-signal-criticality--major"
                  aria-hidden="true"
                />
                Major
              </button>
              <button
                className={`preset-btn ${isCriticalityActive('minor') ? 'active' : ''}`}
                onClick={() => toggleCriticalityFilter('minor')}
                title="Minor devices — matches blue circle marker on device name"
              >
                <span
                  className="device-signal device-signal-criticality device-signal-criticality--minor"
                  aria-hidden="true"
                />
                Minor
              </button>
              <MoreFiltersButton
                isOpen={showAdvancedPanel}
                hasAdditional={hasAdditionalFilters}
                onClick={() => {
                  if (!showAdvancedPanel) {
                    setDrawerFilters({ ...filters });
                  }
                  setShowAdvancedPanel(!showAdvancedPanel);
                }}
                onHelpClick={() => setShowFilterHelp(true)}
              />
            </div>
          </div>

          {/* ACTIVE FILTERS DISPLAY - Only shown when filters are active */}
          {hasAnyFiltersApplied() && (
            <div className="active-filters">
              <div className="active-filters-header">
                <span className="active-filters-label">Filters Active:</span>
                <button
                  className="clear-all-btn"
                  onClick={() => {
                    // Preserve free-text search — clear only structured filters
                    setFilters({ ...createInitialFilterState(), search: filters.search });
                    setActiveFilterOrder([]);
                  }}
                >
                  Clear All
                </button>
              </div>

              <div className="active-filters-pills">
                <DeviceActiveFilterPills
                  filters={filters}
                  setFilters={setFilters}
                  activeFilterOrder={activeFilterOrder}
                  locations={locations}
                />
              </div>
            </div>
          )}

        </>
      )}

      {/* REACTDOM PORTAL - Advanced Filters Modal */}
      {showAdvancedPanel && !showForm && !showDeviceView &&
        ReactDOM.createPortal(
          <div className="sidebar-drawer-overlay" onClick={() => setShowAdvancedPanel(false)}>
            <div className="sidebar-drawer" onClick={(e) => e.stopPropagation()}>
              <div className="drawer-title">
                <span>🔧 Advanced Filters</span>
                <button
                  type="button"
                  className="drawer-close-btn"
                  onClick={() => setShowAdvancedPanel(false)}
                  title="Close filters"
                >
                  ✕
                </button>
              </div>

              <div className="drawer-filter-content">
              {/* LOCATION FILTER */}
              {user?.role === 'superadmin' && (
                <div className="filter-group">
                  <div
                    className="filter-group-title"
                    onClick={() => setExpandedSections(prev => ({ ...prev, location: !prev.location }))}
                  >
                    <span className={`group-arrow ${expandedSections.location ? 'open' : ''}`}>▼</span>
                    <FilterSectionHeader
                      icon="📍"
                      title="Location"
                      tooltip="Select multiple locations. Devices from ANY selected location are shown (OR logic)"
                    />
                  </div>
                  {expandedSections.location && (
                    <div className="filter-group-content">
                      <div className="filter-item-wrapper">
                        <LocationFilterMultiSelect
                          locations={locations}
                          value={drawerFilters.locations || []}
                          onChange={(selected) =>
                            setDrawerFilters({
                              ...drawerFilters,
                              locations: selected,
                            })
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* DEPARTMENT FILTER */}
              <div className="filter-group">
                <div
                  className="filter-group-title"
                  onClick={() => setExpandedSections(prev => ({ ...prev, department: !prev.department }))}
                >
                  <span className={`group-arrow ${expandedSections.department ? 'open' : ''}`}>▼</span>
                  <FilterSectionHeader
                    icon="🏥"
                    title="Department"
                    tooltip="Select multiple departments. Devices from ANY selected department are shown (OR logic)"
                  />
                </div>
                {expandedSections.department && (
                  <div className="filter-group-content">
                    <div className="filter-item-wrapper">
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.departments || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setDrawerFilters({
                            ...drawerFilters,
                            departments: selected
                          });
                        }}
                        style={{ minHeight: '80px' }}
                        disabled={user && ['coordinator', 'custodian', 'technician'].includes(user.role) && user.departments && user.departments.length === 1}
                        title={user && ['coordinator', 'custodian', 'technician'].includes(user.role) && user.departments && user.departments.length === 1 ? 'Only your assigned department is available' : ''}
                      >
                        {/* For department-scoped users (coordinator, custodian, technician): ONLY show their assigned departments */}
                        {/* Managers are location-scoped, so they see all departments like admin/central_help_desk */}
                        {user && ['coordinator', 'custodian', 'technician'].includes(user.role) ? (
                          user.departments && user.departments.length > 0 ? (
                            user.departments.map((dept) => (
                              <option key={dept.id} value={dept.name}>
                                {dept.icon} {dept.name}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>No departments assigned</option>
                          )
                        ) : (
                          /* For admin/manager/superadmin/central_help_desk: show all departments from API fetch */
                          departments.map(dept => (
                            <option key={dept.id} value={dept.name}>{dept.icon} {dept.name}</option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* COMPLAINT FILTERS */}
              <div className="filter-group">
                <div
                  className="filter-group-title"
                  onClick={() => setExpandedSections(prev => ({ ...prev, complaintFilters: !prev.complaintFilters }))}
                >
                  <span className={`group-arrow ${expandedSections.complaintFilters ? 'open' : ''}`}>▼</span>
                  <FilterSectionHeader
                    icon="⚠️"
                    title="Complaint Filters"
                    tooltip="Filter devices by complaint activity and clinical availability impact"
                  />
                </div>
                {expandedSections.complaintFilters && (
                  <div className="filter-group-content">
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">Complaint Activity</div>
                      <select
                        className="filter-select"
                        value={
                          drawerFilters.hasActiveComplaints
                            ? 'active'
                            : drawerFilters.hasNoActiveComplaints
                              ? 'none'
                              : ''
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          setDrawerFilters({
                            ...drawerFilters,
                            hasActiveComplaints: value === 'active',
                            hasNoActiveComplaints: value === 'none',
                          });
                        }}
                      >
                        <option value="">All</option>
                        <option value="active">Has active complaints</option>
                        <option value="none">No active complaints</option>
                      </select>
                    </div>

                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">Availability Impact</div>
                      <select
                        className="filter-select"
                        value={drawerFilters.impactsAvailability || ''}
                        onChange={(e) => {
                          setDrawerFilters({
                            ...drawerFilters,
                            impactsAvailability: e.target.value,
                          });
                        }}
                      >
                        {AVAILABILITY_IMPACT_FILTER_OPTIONS.map((option) => (
                          <option key={option.value || 'all'} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* MAINTENANCE STATUS */}
              <div className="filter-group">
                <div
                  className="filter-group-title"
                  onClick={() => setExpandedSections(prev => ({ ...prev, maintenanceStatus: !prev.maintenanceStatus }))}
                >
                  <span className={`group-arrow ${expandedSections.maintenanceStatus ? 'open' : ''}`}>▼</span>
                  <FilterSectionHeader
                    icon="🔧"
                    title="Maintenance Status"
                    tooltip="Within each maintenance type (PPM, Calibration, Compliance), select multiple statuses. Devices matching ANY selected status are shown (OR logic)"
                  />
                </div>
                {expandedSections.maintenanceStatus && (
                  <div className="filter-group-content">
                    {/* PPM Status */}
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">📦 PPM Status</div>
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.ppmStatus || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setDrawerFilters({ ...drawerFilters, ppmStatus: selected });
                        }}
                        style={{ minHeight: '120px' }}
                      >
                        <option value="Up to Date & Compliant (>30d)">Up to Date & Compliant (>30d)</option>
                        <option value="Due - Expiring Soon (≤30d)">Due - Expiring Soon (≤30d)</option>
                        <option value="Overdue (0-30d)">Overdue (0-30 Days)</option>
                        <option value="Overdue (30+d)">Overdue (30+ Days)</option>
                        <option value="NotSet">Not Set</option>
                      </select>
                    </div>

                    {/* Calibration Status */}
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">⚖️ Calibration</div>
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.calibrationStatus || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setDrawerFilters({ ...drawerFilters, calibrationStatus: selected });
                        }}
                        style={{ minHeight: '120px' }}
                      >
                        <option value="Up to Date & Compliant (>30d)">Up to Date & Compliant (>30d)</option>
                        <option value="Due - Expiring Soon (≤30d)">Due - Expiring Soon (≤30d)</option>
                        <option value="Overdue (0-30d)">Overdue (0-30 Days)</option>
                        <option value="Overdue (30+d)">Overdue (30+ Days)</option>
                        <option value="NotRequired">Not Required</option>
                      </select>
                    </div>

                    {/* Compliance Status */}
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">✅ Compliance</div>
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.complianceStatus || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setDrawerFilters({ ...drawerFilters, complianceStatus: selected });
                        }}
                        style={{ minHeight: '120px' }}
                      >
                        <option value="Up to Date & Compliant (>30d)">Up to Date & Compliant (>30d)</option>
                        <option value="Due - Expiring Soon (≤30d)">Due - Expiring Soon (≤30d)</option>
                        <option value="Overdue (0-30d)">Overdue (0-30 Days)</option>
                        <option value="Overdue (30+d)">Overdue (30+ Days)</option>
                        <option value="NotRequired">Not Required</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* COVERAGE STATUS */}
              <div className="filter-group">
                <div
                  className="filter-group-title"
                  onClick={() => setExpandedSections(prev => ({ ...prev, coverageStatus: !prev.coverageStatus }))}
                >
                  <span className={`group-arrow ${expandedSections.coverageStatus ? 'open' : ''}`}>▼</span>
                  <FilterSectionHeader
                    icon="📋"
                    title="Coverage Status"
                    tooltip="Select multiple coverage statuses and contract types. Devices matching ANY selected option are shown (OR logic)"
                  />
                </div>
                {expandedSections.coverageStatus && (
                  <div className="filter-group-content">
                    {/* Consolidated Coverage Status (applies to warranty and contract) */}
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">Warranty / Contract</div>
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.coverageStatus || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setDrawerFilters({ ...drawerFilters, coverageStatus: selected });
                        }}
                        style={{ minHeight: '120px' }}
                      >
                        <option value="Active & Compliant (>30d)">Active & Compliant (>30d)</option>
                        <option value="Active - Expiring Soon (≤30d)">Active - Expiring Soon (≤30d)</option>
                        <option value="Expired (0-30d)">Expired (0-30 Days)</option>
                        <option value="Expired (30+d)">Expired (30+ Days)</option>
                        <option value="NotCovered">Not Covered</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* DEVICE CHARACTERISTICS */}
              <div className="filter-group">
                <div
                  className="filter-group-title"
                  onClick={() => setExpandedSections(prev => ({ ...prev, characteristics: !prev.characteristics }))}
                >
                  <span className={`group-arrow ${expandedSections.characteristics ? 'open' : ''}`}>▼</span>
                  <FilterSectionHeader
                    icon="🏢"
                    title="Device Characteristics"
                    tooltip="Select multiple values within subsections. E.g., in Service Contracts: select 'Warranty' OR 'LAMC' to see devices with either type"
                  />
                </div>
                {expandedSections.characteristics && (
                  <div className="filter-group-content">
                    {/* Compliance Required */}
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">Compliance Required</div>
                      <select
                        className="filter-select"
                        value={drawerFilters.complianceRequired || ''}
                        onChange={(e) => setDrawerFilters({ ...drawerFilters, complianceRequired: e.target.value })}
                      >
                        <option value="">All</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>

                    {/* Calibration Required */}
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">Calibration Required</div>
                      <select
                        className="filter-select"
                        value={drawerFilters.calibrationRequired || ''}
                        onChange={(e) => setDrawerFilters({ ...drawerFilters, calibrationRequired: e.target.value })}
                      >
                        <option value="">All</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>

                    {/* Criticality */}
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">Criticality</div>
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.criticality || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setDrawerFilters({ ...drawerFilters, criticality: selected });
                        }}
                        style={{ minHeight: '80px' }}
                      >
                        {DEVICE_CRITICALITY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Service Contract Types */}
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">Service Contracts</div>
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.serviceContractTypes || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setDrawerFilters({ ...drawerFilters, serviceContractTypes: selected });
                        }}
                        style={{ minHeight: '80px' }}
                      >
                        <option value="Warranty">🏥 Warranty</option>
                        <option value="LAMC">🔧 LAMC</option>
                        <option value="CAMC">⚙️ CAMC</option>
                        <option value="In-house">🏢 In-house</option>
                      </select>
                    </div>

                    {/* Device Status */}
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">Device Status</div>
                      <select
                        className="filter-select"
                        multiple
                        value={drawerFilters.deviceStatus || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          setDrawerFilters({ ...drawerFilters, deviceStatus: selected });
                        }}
                        style={{ minHeight: '60px' }}
                        title="Draft and pending approval devices are managed in Approvals"
                      >
                        <option value="active">🟢 Active</option>
                        <option value="in_transfer">🟠 In Transfer</option>
                      </select>
                    </div>

                    {/* Cost Range */}
                    <div className="filter-item-wrapper full-width">
                      <div className="filter-label-text">Cost Range</div>
                      <div className="filter-range">
                        <input
                          type="number"
                          placeholder="Min"
                          value={drawerFilters.productCostFrom || ''}
                          onChange={(e) => setDrawerFilters({ ...drawerFilters, productCostFrom: e.target.value ? Number(e.target.value) : null })}
                        />
                        <span className="filter-range-separator">-</span>
                        <input
                          type="number"
                          placeholder="Max"
                          value={drawerFilters.productCostTo || ''}
                          onChange={(e) => setDrawerFilters({ ...drawerFilters, productCostTo: e.target.value ? Number(e.target.value) : null })}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* INSTALLATION DATE RANGE */}
              <div className="filter-group">
                <div
                  className="filter-group-title"
                  onClick={() => setExpandedSections(prev => ({ ...prev, dateRange: !prev.dateRange }))}
                >
                  <span className={`group-arrow ${expandedSections.dateRange ? 'open' : ''}`}>▼</span>
                  <FilterSectionHeader
                    icon="📅"
                    title="Installation Date Range"
                    tooltip="Set From and To dates. Both dates apply (AND logic). Shows devices installed within your selected date range"
                  />
                </div>
                {expandedSections.dateRange && (
                  <div className="filter-group-content">
                    {/* From Date */}
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">From</div>
                      <input
                        type="date"
                        className="filter-select"
                        style={{ padding: '0.5rem 0.75rem' }}
                        value={drawerFilters.installationDateFrom || ''}
                        onChange={(e) => setDrawerFilters({ ...drawerFilters, installationDateFrom: e.target.value })}
                      />
                    </div>

                    {/* To Date */}
                    <div className="filter-item-wrapper">
                      <div className="filter-label-text">To</div>
                      <input
                        type="date"
                        className="filter-select"
                        style={{ padding: '0.5rem 0.75rem' }}
                        value={drawerFilters.installationDateTo || ''}
                        onChange={(e) => setDrawerFilters({ ...drawerFilters, installationDateTo: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>
              </div>

              {/* ACTION BUTTONS */}
              <div className="drawer-buttons">
                <button
                  className="cancel-btn"
                  onClick={() => {
                    setShowAdvancedPanel(false);
                  }}
                  title="Close filters without applying changes"
                >
                  Close
                </button>
                <button
                  className="reset-btn"
                  onClick={() => {
                    // Reset drawer filters only
                    setDrawerFilters(createInitialFilterState());
                  }}
                >
                  Reset
                </button>
                <button
                  className="apply-btn"
                  onClick={() => {
                    // Validate Cost Range
                    if (drawerFilters.productCostFrom !== null && drawerFilters.productCostTo !== null) {
                      if (drawerFilters.productCostFrom > drawerFilters.productCostTo) {
                        toast.error('Minimum cost cannot be greater than Maximum cost');
                        return;
                      }
                    }

                    // Validate Installation Date Range
                    if (drawerFilters.installationDateFrom && drawerFilters.installationDateTo) {
                      if (drawerFilters.installationDateFrom > drawerFilters.installationDateTo) {
                        toast.error('Installation From date cannot be greater than To date');
                        return;
                      }
                    }

                    // Validate Warranty Date Range
                    if (drawerFilters.warrantyDateFrom && drawerFilters.warrantyDateTo) {
                      if (drawerFilters.warrantyDateFrom > drawerFilters.warrantyDateTo) {
                        toast.error('Warranty From date cannot be greater than To date');
                        return;
                      }
                    }

                    // Validate Contract Date Range
                    if (drawerFilters.contractDateFrom && drawerFilters.contractDateTo) {
                      if (drawerFilters.contractDateFrom > drawerFilters.contractDateTo) {
                        toast.error('Contract From date cannot be greater than To date');
                        return;
                      }
                    }

                    // Apply drawerFilters to main filters
                    setFilters(drawerFilters);
                    setShowAdvancedPanel(false);
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      }

      {/* Main Content Container for List */}
      {!showForm && !showDeviceView && (
        <>
          {/* SELECTION INFO & QR BUTTON - Appears when devices are selected */}
          {selectedDeviceIds.size > 0 && (
            <div className="device-qr-selection-bar" role="status">
              <div className="device-qr-selection-bar-text">
                {selectAllDevices ? (
                  <>
                    <strong>{selectedDeviceIds.size}</strong> devices selected for QR labels
                    <span> (all matching current filters)</span>
                  </>
                ) : (
                  <>
                    <strong>{selectedDeviceIds.size}</strong>
                    {` device${selectedDeviceIds.size !== 1 ? 's' : ''} selected for QR labels`}
                  </>
                )}
              </div>
              <div className="device-qr-selection-bar-actions">
                <button
                  type="button"
                  className="device-qr-selection-clear"
                  onClick={() => {
                    setSelectedDeviceIds(new Set());
                    setSelectAllDevices(false);
                  }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="device-qr-selection-print"
                  onClick={handleGenerateBulkQRCodes}
                  disabled={isBulkQRGenerating}
                  title={
                    selectAllDevices
                      ? `Print QR labels for all ${selectedDeviceIds.size} devices matching current filters`
                      : `Print QR labels for ${selectedDeviceIds.size} selected device${selectedDeviceIds.size !== 1 ? 's' : ''}`
                  }
                >
                  {isBulkQRGenerating
                    ? 'Preparing QR labels...'
                    : `Print QR Labels (${selectedDeviceIds.size})`}
                </button>
              </div>
            </div>
          )}

          {isDisabledLocationFilterBlocked && (
            <DisabledLocationAccessBlockedBanner disabledLocations={blockedDisabledLocations} />
          )}

          <DeviceList
            devices={devices}
            totalDevices={pagination.total_count}
            onEdit={handleEditDevice}
            onDelete={handleDeleteDevice}
            onView={handleViewDevice}
            onReportComplaint={handleReportComplaint}
            onViewComplaint={() => {
              if (onNavigateToComplaintsWithFilter) {
                onNavigateToComplaintsWithFilter({});
              }
            }}
            token={token}
            hasFiltersApplied={hasAnyFiltersApplied()}
            onExportExcel={handleExportDevicesExcel}
            onExportPDF={handleExportDevicesPDF}
            onExportMaintenancePDF={() => {}}
            user={user}
            isRefreshing={refreshStatus || isExportingDevices}
            exportTotalCount={pagination.total_count}
            onViewMaintenanceHistory={(filterOptions) => {
              if (onNavigateToMaintenanceHistory) {
                onNavigateToMaintenanceHistory(filterOptions);
              }
            }}
            onNavigateToAlertsWithFilter={(filterOptions) => {
              if (onNavigateToAlertsWithFilter) {
                onNavigateToAlertsWithFilter(filterOptions);
              }
            }}
            sorting={sorting}
            onSortChange={handleSortChange}
            selectedDeviceIds={selectedDeviceIds}
            selectAllDevices={selectAllDevices}
            isSelectingAllDevices={isSelectingAllDevices}
            onToggleDeviceSelection={handleToggleDeviceSelection}
            onToggleSelectAll={handleToggleSelectAll}
          >
            {pagination.total_count > 0 && (
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.total_pages}
                onPageChange={(newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
                perPage={pagination.per_page}
                onPerPageChange={(newPerPage) => {
                  saveUiPreferencePatch({ list_page_size: newPerPage });
                  setPagination(prev => ({ ...prev, page: 1, per_page: newPerPage }));
                  fetchDevices({ page: 1, per_page: newPerPage });
                }}
                totalItems={pagination.total_count}
                itemLabel="devices"
              />
            )}
          </DeviceList>
        </>
      )}

      {/* Barcode Scanner Modal */}
      {showScanner && (
        <BarcodeScanner
          key={scannerKey}
          onScan={handleScanDevice}
          onClose={() => setShowScanner(false)}
          title="Scan Device"
          subtitle="Align the QR code within the frame to look up a device."
        />
      )}

      {/* Complaint Form Modal */}
      {showComplaintForm && complaintDevice && (
        <ComplaintForm
          device={complaintDevice}
          token={token}
          onClose={() => {
            setShowComplaintForm(false);
            setComplaintDevice(null);
          }}
          onSuccess={handleComplaintSuccess}
          user={user}
        />
      )}

      {complaintConfirmation && (
        <ComplaintConfirmation
          confirmation={complaintConfirmation}
          user={user}
          onClose={() => setComplaintConfirmation(null)}
          onViewComplaint={
            onNavigateToComplaintDetail
              ? (id) => {
                  setComplaintConfirmation(null);
                  onNavigateToComplaintDetail(id);
                }
              : undefined
          }
        />
      )}

      {/* Filter Help Modal */}
      <FilterHelpModal
        isOpen={showFilterHelp}
        onClose={() => setShowFilterHelp(false)}
        filterType="devices"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        entityName={deviceToDelete?.name || 'Device'}
        entityType="device"
        action="delete"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeletingDevice}
        customMessage="This device and all associated data (documents, maintenance history, complaints) will be permanently removed from the system. This action cannot be undone."
      />

      {/* Hidden QRBatchRenderer - renders one chunk at a time during PDF generation */}
      {showQRBatchRenderer && (
        <QRBatchRenderer
          bulkQRTokens={qrCaptureTokens}
          isVisible={showQRBatchRenderer}
          errorCorrectionLevel={qrErrorCorrectionLevel}
          onRenderComplete={() => {
            qrBatchRenderingCompleteRef.current = true;
          }}
        />
      )}

      {/* Bulk QR Labels Modal - Preview at Print Size */}
      {showBulkQRModal && bulkQRTokens.length > 0 && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={!isGeneratingPDF ? handleCloseBulkQRModal : undefined}>
          <div className="bulk-qr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>QR Labels Preview ({bulkQRTokens.length})</h3>
              {!isGeneratingPDF && (
                <ModalCloseButton onClick={handleCloseBulkQRModal} title="Close" />
              )}
            </div>

            {isGeneratingPDF ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '3rem 2rem',
                gap: '1.5rem',
                minHeight: '300px'
              }}>
                <div style={{ fontSize: '2.5rem', animation: 'pulse 1.5s ease-in-out infinite' }}>⏳</div>
                <div style={{ textAlign: 'center' }}>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>
                    {isCancellingPDF ? 'Cancelling…' : 'Preparing QR Labels'}
                  </h3>
                  <p style={{ margin: '0 0 1.5rem 0', color: '#666', fontSize: '0.95rem' }}>
                    {isCancellingPDF
                      ? 'Stopping after the current label…'
                      : (
                        <>
                          {pdfProgress.stage === 'initializing' && 'Initializing...'}
                          {pdfProgress.stage === 'rendering' && 'Rendering QR labels...'}
                          {pdfProgress.stage === 'capturing' && `Capturing QR labels (${pdfProgress.processed} of ${pdfProgress.total})`}
                          {pdfProgress.stage === 'layout' && `Adding to PDF (${pdfProgress.processed} of ${pdfProgress.total})`}
                          {pdfProgress.stage === 'ready' && 'PDF ready!'}
                          {!pdfProgress.stage && 'Setting up...'}
                        </>
                      )}
                  </p>
                </div>

                <div style={{ width: '100%', maxWidth: '400px' }}>
                  {/* Progress bar with animated background when no progress yet */}
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <div style={{
                      height: '100%',
                      backgroundColor: 'var(--dp-accent-primary)',
                      // Start at ~1/total and scale remaining progress (shows 1st item being processed)
                      width: `${pdfProgress.total > 0 ? Math.max((pdfProgress.processed / pdfProgress.total) * 100, 5) : 5}%`,
                      transition: 'width 0.4s ease',
                      // Shimmer while still initializing (before rendering stage starts)
                      animation: pdfProgress.stage === 'initializing' ? 'shimmer 1.5s ease-in-out infinite' : 'none'
                    }} />
                  </div>

                  {/* Progress text */}
                  <div style={{ marginTop: '1rem', textAlign: 'center', color: '#666', fontSize: '0.85rem' }}>
                    {pdfProgress.total > 0 ? (
                      <>
                        <strong>{pdfProgress.processed}</strong> of <strong>{pdfProgress.total}</strong>
                        {pdfProgress.totalPages > 0 && ` (Page ${pdfProgress.currentPage}/${pdfProgress.totalPages})`}
                      </>
                    ) : (
                      <span style={{ fontStyle: 'italic', color: '#999' }}>Preparing files...</span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className="action-btn action-secondary"
                  onClick={handleCancelPDFGeneration}
                  disabled={isCancellingPDF}
                >
                  <span className="action-text">{isCancellingPDF ? 'Cancelling…' : 'Cancel'}</span>
                </button>

                <style>{`
                  @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.6; }
                  }
                  @keyframes shimmer {
                    0% { opacity: 0.6; }
                    50% { opacity: 1; }
                    100% { opacity: 0.6; }
                  }
                `}</style>
              </div>
            ) : (
              <>
                <div className="bulk-qr-content">
                  <div className="bulk-qr-info">
                    <p>
                      {bulkQRTokens.length} QR label{bulkQRTokens.length !== 1 ? 's' : ''} ready to print and apply to devices.
                    </p>
                    <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                      Labels are arranged in a 4×5 grid (20 per page).
                    </p>
                    <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem', fontStyle: 'italic' }}>
                      Tip: Change "QR Quality" below to adjust density.
                      <strong> L = Cleanest (most white space) | M = Balanced | H = Most dense (current: {qrErrorCorrectionLevel})</strong>
                    </p>
                    <p style={{ fontSize: '0.85rem', color: '#3629e8', marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fef3c7', borderRadius: '4px', borderLeft: '3px solid #d97706' }}>
                      <strong>Recommended filename:</strong>
                      <div style={{
                        backgroundColor: '#fff3cd',
                        padding: '0.4rem',
                        borderRadius: '2px',
                        fontSize: '0.75rem',
                        marginTop: '0.01rem',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        fontFamily: 'monospace'
                      }}>
                        &lt;org&gt;_&lt;location&gt;_BulkQRLabels_&lt;date&gt;_&lt;time&gt;.pdf
                      </div>
                      When you click "Print QR Labels" and save, use this naming format for easy organization and tracking.
                    </p>
                  </div>

                  {/* QR Codes Grid at Print Size */}
                  <div className="bulk-qr-preview-grid" ref={bulkQRPreviewGridRef}>
                      {bulkQRTokens.map((item, index) => (
                        <div key={index} className="qr-preview-card">
                          {/* Device Identifier at Top */}
                          <div className="qr-preview-id">
                            {item.device_identifier}
                          </div>

                          {/* QR Code at Print Size - Uses selected error correction level */}
                          <div className="qr-preview-code">
                            <QRCode
                              value={item.qr_url}
                              size={100}
                              level={qrErrorCorrectionLevel}
                              includeMargin
                            />
                          </div>

                          {/* Device Info Below QR Code - Matches Single QR Modal Format */}
                          <div className="qr-preview-info">
                            <table className="qr-preview-info-table">
                              <colgroup>
                                <col className="qr-preview-label-col" />
                                <col />
                              </colgroup>
                              <tbody>
                                <tr>
                                  <td className="qr-preview-label">Equipment:</td>
                                  <td className="qr-preview-value">{item.device_name || 'Unknown'}</td>
                                </tr>
                                <tr>
                                  <td className="qr-preview-label">Dept:</td>
                                  <td className="qr-preview-value">{item.department || 'N/A'}</td>
                                </tr>
                                <tr>
                                  <td className="qr-preview-label">Model:</td>
                                  <td className="qr-preview-value">{item.model || 'N/A'}</td>
                                </tr>
                                <tr>
                                  <td className="qr-preview-label">SN:</td>
                                  <td className="qr-preview-value">{item.serial_number || 'N/A'}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                <div className="bulk-qr-footer">
                  <div className="bulk-qr-quality">
                    <label htmlFor="bulk-qr-quality-select">QR Quality</label>
                    <select
                      id="bulk-qr-quality-select"
                      value={qrErrorCorrectionLevel}
                      onChange={(e) => setQrErrorCorrectionLevel(e.target.value)}
                      disabled={isGeneratingPDF}
                      title="Change QR code error correction level: L=Cleanest, M=Balanced, Q=Robust, H=Most Robust"
                    >
                      <option value="L">L - Low (7% redundancy) - Cleanest appearance</option>
                      <option value="M">M - Medium (15% redundancy) - Balanced (Recommended)</option>
                      <option value="Q">Q - Quartile (25% redundancy) - High redundancy</option>
                      <option value="H">H - High (30% redundancy) - Most robust, dense</option>
                    </select>
                    <span className="bulk-qr-quality-hint">
                      {qrErrorCorrectionLevel === 'L' && 'Cleanest'}
                      {qrErrorCorrectionLevel === 'M' && 'Balanced'}
                      {qrErrorCorrectionLevel === 'Q' && 'Robust'}
                      {qrErrorCorrectionLevel === 'H' && 'Most robust'}
                    </span>
                  </div>

                  <div className="bulk-qr-footer-actions">
                    <button
                      type="button"
                      className="action-btn action-secondary"
                      onClick={handleCloseBulkQRModal}
                      disabled={isGeneratingPDF}
                    >
                      <span className="action-text">Close</span>
                    </button>
                    <button
                      type="button"
                      className="action-btn action-primary"
                      onClick={handleDownloadBulkPDF}
                      disabled={isGeneratingPDF}
                      title={`Download QR labels PDF (${qrErrorCorrectionLevel} error correction)`}
                    >
                      <span className="action-text">
                        {isGeneratingPDF ? 'Preparing...' : `Download QR Labels (${qrErrorCorrectionLevel})`}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="action-btn action-primary"
                      onClick={handleConfirmAndGeneratePDF}
                      disabled={isGeneratingPDF}
                      title={`Print QR labels with ${qrErrorCorrectionLevel} error correction`}
                    >
                      <span className="action-text">
                        {isGeneratingPDF ? 'Preparing...' : `Print QR Labels (${qrErrorCorrectionLevel})`}
                      </span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default Devices;
